'use client';

import { useEffect, useRef, useState } from 'react';
import type { ImageField, RichTextField } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import './partners-section.css';

/**
 * "Our Partners": NetSuite as a dark core panel with partner capability cards
 * on either side, wired to it by curves that a glowing packet travels along,
 * lighting each card's logos in turn. Below the compact breakpoint the core
 * stacks on top and the cards hang off a vertical rail. Ported from the
 * OnePacific Claude Design comp "Partners Section Comps".
 */

export interface PartnerLogo {
  /** alt text should already fall back to the company name */
  image: ImageField;
}

export interface PartnerCard {
  eyebrow?: string;
  heading?: string;
  logos: PartnerLogo[];
}

export interface PartnersSectionProps {
  /** dark-teal theme (slice variation "inverted") */
  inverted?: boolean;
  eyebrow?: string;
  title?: string;
  titleAccent?: string;
  lede?: RichTextField;
  coreEyebrow?: string;
  coreLogo?: PartnerLogo;
  coreText?: string;
  cards: PartnerCard[];
}

interface Wire {
  d: string;
  x: number;
  y: number;
}

// Logo docs are padded onto a 1000×500 transparent canvas; trim it so the
// rendered height is the mark itself.
const TRIM = { trim: 'color' } as const;

function Logo({ logo, className }: { logo: PartnerLogo; className: string }) {
  return (
    <PrismicNextImage
      field={logo.image}
      imgixParams={TRIM}
      className={className}
    />
  );
}

const TRAVEL = 1600;
const HOLD = 700;
const GAP = 300;
const SLOT = TRAVEL + HOLD + GAP;

export default function PartnersSection({
  inverted = false,
  eyebrow,
  title,
  titleAccent,
  lede,
  coreEyebrow,
  coreLogo,
  coreText,
  cards,
}: PartnersSectionProps) {
  const hubRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [wires, setWires] = useState<{ w: number; h: number; list: Wire[] }>({ w: 0, h: 0, list: [] });
  const [inView, setInView] = useState(false);

  const half = Math.ceil(cards.length / 2);
  const left = cards.slice(0, half);
  const right = cards.slice(half);

  // Measure wires (desktop) and the rail end (compact) whenever the hub resizes.
  useEffect(() => {
    const hub = hubRef.current;
    const svg = svgRef.current;
    if (!hub || !svg) return;
    const measure = () => {
      const hb = hub.getBoundingClientRect();
      const cardEls = [...hub.querySelectorAll<HTMLElement>('.pn-card')];

      // Compact: the rail runs down from the core and stops at the last card's centre.
      const cols = [...hub.querySelectorAll<HTMLElement>('.pn-col')];
      const lastCol = [...cols].reverse().find((c) => c.querySelector('.pn-card'));
      cols.forEach((col) => {
        const rail = col.querySelector<HTMLElement>('.pn-rail');
        if (!rail) return;
        if (col !== lastCol) return void (rail.style.bottom = '');
        const last = [...col.querySelectorAll<HTMLElement>('.pn-card')].pop()!;
        const lr = last.getBoundingClientRect();
        rail.style.bottom = `${col.getBoundingClientRect().bottom - (lr.top + lr.height / 2)}px`;
      });

      if (getComputedStyle(svg).display === 'none') {
        setWires((w) => (w.list.length ? { w: 0, h: 0, list: [] } : w));
        return;
      }
      const core = hub.querySelector<HTMLElement>('.pn-core');
      if (!core) return;
      const cb = core.getBoundingClientRect();
      const cy = cb.top - hb.top + cb.height / 2;
      const list = cardEls.map((c) => {
        const r = c.getBoundingClientRect();
        const isLeft = r.left < cb.left;
        const x1 = isLeft ? cb.left - hb.left : cb.right - hb.left;
        const x2 = isLeft ? r.right - hb.left : r.left - hb.left;
        const y2 = r.top - hb.top + r.height / 2;
        const mx = (x1 + x2) / 2;
        return { d: `M${x1},${cy} C${mx},${cy} ${mx},${y2} ${x2},${y2}`, x: x2, y: y2 };
      });
      const next = { w: hb.width, h: hb.height, list };
      setWires((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(hub);
    hub.querySelectorAll('.pn-col').forEach((c) => ro.observe(c));
    return () => ro.disconnect();
  }, [cards.length]);

  // Only animate while on screen.
  useEffect(() => {
    const hub = hubRef.current;
    if (!hub) return;
    if (typeof IntersectionObserver === 'undefined') {
      const t = setTimeout(() => setInView(true), 0);
      return () => clearTimeout(t);
    }
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.2 });
    io.observe(hub);
    return () => io.disconnect();
  }, []);

  // Packet loop: one card at a time — travel the wire, light the card, fade.
  const wireCount = wires.list.length;
  useEffect(() => {
    const hub = hubRef.current;
    const svg = svgRef.current;
    if (!hub || !svg || !wireCount || !inView) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const cardEls = [...hub.querySelectorAll<HTMLElement>('.pn-card')];
    const paths = [...svg.querySelectorAll<SVGPathElement>('path')];
    const pks = [...svg.querySelectorAll<SVGCircleElement>('.pn-pk')];
    if (paths.length !== cardEls.length || pks.length !== cardEls.length) return;
    const n = cardEls.length;
    const t0 = performance.now();
    let raf = 0;
    const frame = (now: number) => {
      const t = (now - t0) % (SLOT * n);
      const i = Math.floor(t / SLOT);
      const s = t - i * SLOT;
      pks.forEach((pk, j) => {
        if (j !== i) {
          pk.setAttribute('opacity', '0');
          cardEls[j].classList.remove('pn-hit');
          return;
        }
        const len = paths[j].getTotalLength();
        const pt = paths[j].getPointAtLength(len * Math.min(1, s / TRAVEL));
        pk.setAttribute('cx', String(pt.x));
        pk.setAttribute('cy', String(pt.y));
        pk.setAttribute('opacity', String(s < TRAVEL ? 1 : Math.max(0, 1 - (s - TRAVEL) / HOLD)));
        cardEls[j].classList.toggle('pn-hit', s >= TRAVEL && s < TRAVEL + HOLD);
      });
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      cardEls.forEach((c) => c.classList.remove('pn-hit'));
      pks.forEach((pk) => pk.setAttribute('opacity', '0'));
    };
  }, [wires, wireCount, inView]);

  const renderCol = (list: PartnerCard[]) => (
    <div className="pn-col">
      <span className="pn-rail" aria-hidden="true" />
      {list.map((c, i) => (
        <div className="pn-card" key={i}>
          {c.eyebrow && <div className="pn-card-eyebrow">{c.eyebrow}</div>}
          {c.heading && <h3 className="pn-card-heading">{c.heading}</h3>}
          {c.logos.length > 0 && (
            <div className="pn-logos">
              {c.logos.map((l, j) => (
                <Logo key={j} logo={l} className="pn-logo" />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <section className={`pn${inverted ? ' pn--dark' : ''}`}>
      <div className="pn-inner">
        {eyebrow && <p className="pn-eyebrow">{eyebrow}</p>}
        {(title || titleAccent) && (
          <h2 className="pn-title">
            {title}
            {title && titleAccent && ' '}
            {titleAccent && <span className="pn-accent">{titleAccent}</span>}
          </h2>
        )}
        {lede && (
          <div className="pn-lede">
            <PrismicRichText field={lede} />
          </div>
        )}

        <div className="pn-hub" ref={hubRef}>
          <svg
            ref={svgRef}
            className="pn-wires"
            aria-hidden="true"
            viewBox={wires.w ? `0 0 ${wires.w} ${wires.h}` : undefined}
          >
            {wires.list.map((w, i) => (
              <g key={i}>
                <path d={w.d} />
                <circle className="pn-end" cx={w.x} cy={w.y} r={4} />
                <circle className="pn-pk" r={3.5} opacity={0} />
              </g>
            ))}
          </svg>
          {renderCol(left)}
          <div className="pn-core">
            {coreEyebrow && <div className="pn-core-eyebrow">{coreEyebrow}</div>}
            {coreLogo && <Logo logo={coreLogo} className="pn-core-logo" />}
            {coreText && <p className="pn-core-text">{coreText}</p>}
          </div>
          {right.length > 0 && renderCol(right)}
        </div>
      </div>
    </section>
  );
}
